import {
  Page,
  LoadingModal,
  TabsList,
  NotFound,
  SlimButton,
  Card,
  StatisticsCard,
  Icon
} from 'components'
import { Filter } from './components/Filter'
import { useDispatch, useSelector } from 'store/store'
import { useEffect, useState } from 'react'
import VolumeTable from './components/VolumeTable'
import { useForm, FormProvider } from 'react-hook-form'
import { TipoCriticaVolume } from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import {
  asyncGetExtraVolumeData,
  asyncGetRejectMotiveList,
  setMaterialList
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import { numberToDecimalString } from 'commons/utils/number'
import { CentersFactories } from 'constants/enums'
import UploadAnalysisErrorModal from './components/UploadAnalysisErrorModal'

import * as S from './styles'

export const splittingString = (value: string) => {
  return value.length > 0 ? value.split(',') : []
}

export const ExtraVolumePage = () => {
  const dispatch = useDispatch()
  const [hasFilter, setHasFilter] = useState(false)

  const { materialsList, loadingExtraVolume, totalizer, rejectionMotives, uploadErrors } =
    useSelector((store) => store.extraVolume)

  const defaultValues = {
    TipoCritica: [] as TipoCriticaVolume[],
    Materias: [], // TODO: check if this is correct
    Mercado: [],
    EscritorioVenda: [],
    EquipeVenda: [], // Equipes
    RegiaoVenda: [], // Zona (Regioes)
    CodigoCentroMaterial: CentersFactories.A022 as string,
    MesesCota: [],
    numeroPagina: 0,
    registrosPaginas: 20
  }

  const methods = useForm({
    defaultValues
  })

  const getData = (obj: typeof defaultValues, totalizer = true) => {
    const {
      TipoCritica,
      Materias,
      Mercado,
      EscritorioVenda,
      EquipeVenda,
      RegiaoVenda,
      MesesCota
    } = obj
    const emptyFilters = [
      TipoCritica,
      Materias,
      Mercado,
      EscritorioVenda,
      EquipeVenda,
      RegiaoVenda,
      MesesCota
    ].every((i) => i[0] === '')
    setHasFilter(!emptyFilters)

    dispatch(asyncGetExtraVolumeData(obj, totalizer))
  }

  const handlePageChange = (page: number) => {
    getData(
      {
        ...methods.getValues(),
        numeroPagina: page - 1
      },
      false
    )
  }

  useEffect(() => {
    getData(methods.getValues())
    if (!rejectionMotives?.length) {
      dispatch(asyncGetRejectMotiveList())
    }
  }, [])

  const onFilterSubmit = () => {
    getData(methods.getValues())
  }

  const onFilterClear = () => {
    methods.reset(defaultValues)
    getData(defaultValues)
  }

  const handleFactoryChange = (name: string) => {
    dispatch(setMaterialList([]))
    const newFilter = { ...methods.getValues(), CodigoCentroMaterial: name }
    const factory = totalizer.TotalizadorPorCentro.find(
      (t) => t.CodigoCentroMaterial === name
    )
    if (factory?.QuantidadePares) {
      getData(newFilter, false)
    }
    methods.setValue('CodigoCentroMaterial', name)
  }

  const notFoundContent = () => (
    <S.NotFoundWrapper>
      {!hasFilter ? (
        <NotFound
          title={`Nenhuma análise pendente nesta fábrica`}
          icon={<Icon icon="empty" size="50" />}
          description={
            <S.NotFoundContentWrapper>
              Verifique novamente mais tarde
            </S.NotFoundContentWrapper>
          }
        />
      ) : (
        <NotFound
          title={`Nenhum resultado encontrado em linha nesta fábrica`}
          description={
            <S.NotFoundContentWrapper>
              Verifique os critérios utilizados e tente novamente.
              <SlimButton
                content="Reiniciar busca e filtros"
                type="button"
                icon={<Icon icon="chevron-left" size="15" />}
                variant="white"
                onClick={onFilterClear}
              />{' '}
            </S.NotFoundContentWrapper>
          }
        />
      )}
    </S.NotFoundWrapper>
  )

  const renderTable = () =>
    materialsList.length ? (
      <VolumeTable
        payload={materialsList}
        handlePageChange={handlePageChange}
        updateTable={() => getData(methods.getValues())}
      />
    ) : (
      notFoundContent()
    )

  return (
    <FormProvider {...methods}>
      <Page fullwidth>
        {loadingExtraVolume ? (
          <LoadingModal />
        ) : (
          <>
            <Filter
              onSubmit={onFilterSubmit}
              onClear={onFilterClear}
              disableFilters={!hasFilter && !materialsList.length}
            />
            <S.WrapStatistics>
              <StatisticsCard
                title="Volume p/ analisar"
                mainValue={numberToDecimalString(totalizer.TotalQuantidadePares)}
                icon={<Icon icon="clock" color="#94A3BC" size="25" />}
              >
                <S.WrapTotalizers>
                  <S.TotalizerContent>
                    <b>Crítica por cota</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorCota)}
                  </S.TotalizerContent>
                  <S.TotalizerContent>
                    <b>Crítica por estoque</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorEstoque)}
                  </S.TotalizerContent>
                  <S.TotalizerContent>
                    <b>Crítica por cota e estoque</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorCotaEstoque)}
                  </S.TotalizerContent>
                </S.WrapTotalizers>
              </StatisticsCard>
            </S.WrapStatistics>
            {totalizer?.TotalizadorPorCentro.length ? (
              <TabsList
                initialTab={totalizer.TotalizadorPorCentro.findIndex(
                  (fab) =>
                    fab.CodigoCentroMaterial === methods.getValues('CodigoCentroMaterial')
                )}
                showTotalizer
                onClick={handleFactoryChange}
              >
                {totalizer?.TotalizadorPorCentro.map((center) => (
                  <TabsList.Tab
                    key={center.CodigoCentroMaterial}
                    label={center.CodigoCentroMaterial}
                    totalizer={center.QuantidadePares}
                    disabled={!center.QuantidadePares}
                    tooltip={
                      !center.QuantidadePares ? (
                        <p>
                          No momento não existem solicitações
                          <br /> pendentes nesta fábrica
                        </p>
                      ) : (
                        <></>
                      )
                    }
                  >
                    {renderTable()}
                  </TabsList.Tab>
                ))}
              </TabsList>
            ) : (
              <Card>{renderTable()}</Card>
            )}
          </>
        )}
        <UploadAnalysisErrorModal data={uploadErrors} />
      </Page>
    </FormProvider>
  )
}

export default ExtraVolumePage
